// =============================================================================
// BookingModal — Context & Provider
// =============================================================================
//
// Single provider that wires together:
//   • useReducer (state management)
//   • All API hooks (queries + mutations)
//   • Derived values (selectedService, slotStart/End, …)
//   • Business-logic handlers (submit, optimize, create customer, …)
//
// The Provider is mounted ONLY when the modal is open (see BookingModal.tsx),
// so no queries fire while the modal is closed.
// =============================================================================

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
  type Dispatch,
} from 'react';
import i18n from '../../../i18n';
import { format, addMinutes, startOfDay, addDays, isBefore, parseISO } from 'date-fns';
import { useTenantTimeZone } from '../../../store/tenantStore';
import {
  addCivilDays,
  civilDateInTz,
  civilDayOf,
  civilDayToCarrier,
  endOfCivilDayInTz,
  startOfCivilDayInTz,
  timeStringInTz,
  wallClockToUtc,
} from '../../../lib/tenantTime';
import { useDateLocale } from '../../../hooks/useDateLocale';
import type { AvailableSlot as AvailableSlotType } from '../../../api/hooks';
import type { ServiceDefinition, Customer, Resource, ResourceType } from '../../../types';
import {
  useServices,
  useCustomers,
  useCreateAppointment,
  useCreateCustomer,
  useAvailableSlots,
  useOptimizeSuggestion,
  useOverrideBooking,
  useResourceTypes,
  useResources,
  useAddParticipant,
  type AvailableSlot,
} from '../../../api/hooks';
import {
  useSelectedSlot,
  useSchedulerActions,
  useSelectedDate,
} from '../../../store/schedulerStore';
import type { BookingState } from './types';
import type { AvailableSlot as AvailableSlotHook } from '../../../api/hooks';
import { bookingReducer, createInitialState, type BookingAction } from './bookingReducer';

// ===========================================================================
// Multi-day suggestion grouping
// ===========================================================================

export interface DaySuggestion {
  date: Date;
  dateLabel: string;
  slots: AvailableSlotHook[];
  firstSlotTime: string;
  slotCount: number;
}

// ===========================================================================
// Context Shape
// ===========================================================================

export interface BookingContextValue {
  // State & dispatch
  state: BookingState;
  dispatch: Dispatch<BookingAction>;

  // API data (read-only)
  services: ServiceDefinition[];
  customers: Customer[];
  resourceTypes: ResourceType[];
  allResources: Resource[];
  servicesLoading: boolean;
  customersLoading: boolean;

  // Availability
  availableSlots: AvailableSlot[];
  slotsLoading: boolean;
  slotsError: boolean;
  refetchSlots: () => void;

  // First available slot (when current day has no results)
  firstAvailableSlot: AvailableSlotType | null;
  firstAvailableLoading: boolean;
  handleGoToFirstAvailable: () => void;

  // Multi-day suggestions (next 7 days with availability)
  multiDaySuggestions: DaySuggestion[];
  multiDaySuggestionsLoading: boolean;
  handleNavigateToSuggestion: (suggestion: DaySuggestion) => void;

  // Mutations (exposed so step components can read isPending, etc.)
  createAppointment: ReturnType<typeof useCreateAppointment>;
  createCustomer: ReturnType<typeof useCreateCustomer>;
  optimizeSuggestion: ReturnType<typeof useOptimizeSuggestion>;
  overrideBooking: ReturnType<typeof useOverrideBooking>;
  addParticipant: ReturnType<typeof useAddParticipant>;

  // Derived
  selectedService: ServiceDefinition | undefined;
  selectedCustomer: Customer | undefined;
  slotStart: Date | null;
  slotEnd: Date | null;

  // Handlers
  handleClose: () => void;
  handleNextStep: () => void;
  handleBackStep: () => void;
  handleSubmit: () => void;
  handleCreateCustomer: () => Promise<void>;
  handleSlotSelect: (slot: AvailableSlot) => void;
  handleDateChange: (direction: 'prev' | 'next') => void;
  handleRequestOptimization: () => Promise<void>;
  handleApplyOptimizationAndBook: () => Promise<void>;
}

const BookingContext = createContext<BookingContextValue | null>(null);

// ===========================================================================
// Hook — consumers call useBooking() to access everything
// ===========================================================================

export function useBooking(): BookingContextValue {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error('useBooking must be used within <BookingProvider>');
  return ctx;
}

// ===========================================================================
// Provider
// ===========================================================================

export function BookingProvider({ children }: { children: ReactNode }) {
  const selectedSlot = useSelectedSlot();
  const selectedDate = useSelectedDate();
  const { closeModal, setDate } = useSchedulerActions();
  const dateLocale = useDateLocale();
  // Tutti gli orari del pannello sono orari dell'organizzazione.
  const timeZone = useTenantTimeZone();

  // ── Reducer ──────────────────────────────────────────────────────────
  // Provider remounts each time the modal opens (guard in BookingModal.tsx),
  // so the lazy initialiser always captures the fresh date/slot.
  // selectedSlot.start e' un istante (arriva dal click sulla griglia);
  // selectedDate e' gia' un carrier di data civile del tenant.
  const initialSlotDate = selectedSlot
    ? civilDayToCarrier(civilDateInTz(selectedSlot.start, timeZone))
    : startOfDay(selectedDate);
  const initialManualTime = selectedSlot
    ? timeStringInTz(selectedSlot.start, timeZone)
    : '';

  const [state, dispatch] = useReducer(bookingReducer, null, () => ({
    ...createInitialState(initialSlotDate),
    manualStartTime: initialManualTime,
  }));

  // ── API queries ──────────────────────────────────────────────────────
  const { data: services = [], isLoading: servicesLoading } = useServices();
  const { data: customersData, isLoading: customersLoading } = useCustomers(
    state.customerSearch || undefined,
  );
  const { data: resourceTypes = [] } = useResourceTypes();
  const { data: allResources = [] } = useResources();

  // ── Mutations ────────────────────────────────────────────────────────
  const createAppointment = useCreateAppointment();
  const createCustomerMut = useCreateCustomer();
  const optimizeSuggestionMut = useOptimizeSuggestion();
  const overrideBookingMut = useOverrideBooking();
  const addParticipantMut = useAddParticipant();

  const customers = customersData?.data || [];

  // ── Derived entities ─────────────────────────────────────────────────
  const selectedService = services.find(s => s.id === state.selectedServiceId);
  const selectedCustomer = customers.find(c => c.id === state.selectedCustomerId);

  // ── Availability params (complex memo) ───────────────────────────────
  const availabilityParams = useMemo(() => {
    if (!state.slotDate) return null;

    // Build customRequirements from per-slot overrides
    const buildCustomRequirements = () => {
      const reqs = state.smartConstraints.overriddenRequirements;
      if (!reqs.length) return undefined;
      if (!reqs.some(r => r.resourceId !== null)) return undefined;
      return reqs.map(r => ({
        resourceTypeId: r.resourceTypeId,
        resourceId: r.resourceId ?? undefined,
        quantity: 1,
      }));
    };

    const sharedConstraints = {
      ...(state.smartConstraints.allowedDays.length > 0 && {
        allowedDays: state.smartConstraints.allowedDays,
      }),
      ...(state.smartConstraints.minTime && { minTime: state.smartConstraints.minTime }),
      ...(state.smartConstraints.maxTime && { maxTime: state.smartConstraints.maxTime }),
      ...(state.smartConstraints.deadlineDate && { deadlineDate: state.smartConstraints.deadlineDate }),
    };

    // ---- Custom event mode ----
    if (state.bookingMode === 'custom') {
      if (!state.customEvent.duration || state.customEvent.duration < 5) return null;
      const customReqs = buildCustomRequirements();
      const baseReqs =
        state.customEvent.resourceRequirements.length > 0
          ? state.customEvent.resourceRequirements.map(r => ({
              resourceTypeId: r.resourceTypeId,
              quantity: r.quantity,
            }))
          : undefined;
      return {
        criteria: {
          duration: state.customEvent.duration,
          customRequirements: customReqs || baseReqs,
        },
        dateRange: {
          start: startOfCivilDayInTz(civilDayOf(state.slotDate), timeZone).toISOString(),
          end: endOfCivilDayInTz(civilDayOf(state.slotDate), timeZone).toISOString(),
        },
        maxSlots: 30,
        ...sharedConstraints,
      };
    }

    // ---- Catalog mode ----
    if (!state.selectedServiceId) return null;
    const customReqs = buildCustomRequirements();
    return {
      serviceDefinitionId: state.selectedServiceId,
      ...(customReqs && { criteria: { customRequirements: customReqs } }),
      dateRange: {
        start: startOfCivilDayInTz(civilDayOf(state.slotDate), timeZone).toISOString(),
        end: endOfCivilDayInTz(civilDayOf(state.slotDate), timeZone).toISOString(),
      },
      maxSlots: 30,
      // Evidenzia gli eventi di gruppo esistenti con posti liberi (solo catalog:
      // per gli eventi custom il concetto di "evento simile" non è definito)
      includeJoinableEvents: true,
      ...sharedConstraints,
    };
  }, [
    state.bookingMode,
    state.selectedServiceId,
    state.slotDate,
    state.customEvent.duration,
    state.customEvent.resourceRequirements,
    state.smartConstraints,
    timeZone,
  ]);

  // ── Availability query ───────────────────────────────────────────────
  const {
    data: availabilityData,
    isLoading: slotsLoading,
    isError: slotsError,
    refetch: refetchSlots,
  } = useAvailableSlots(availabilityParams);

  const availableSlots = availabilityData?.slots || [];

  // ── First-available query (fires when current day has 0 slots) ───────
  const FIRST_AVAIL_SEARCH_DAYS = 30;
  const firstAvailParams = useMemo(() => {
    // Only search when: current day loaded, no error, 0 results, and we have valid params
    if (slotsLoading || slotsError || availableSlots.length > 0) return null;
    if (!availabilityParams) return null;

    const fromDay = civilDayOf(state.slotDate);
    const searchStart = startOfCivilDayInTz(addCivilDays(fromDay, 1), timeZone);
    const searchEnd = endOfCivilDayInTz(
      addCivilDays(fromDay, FIRST_AVAIL_SEARCH_DAYS),
      timeZone,
    );

    return {
      ...availabilityParams,
      dateRange: {
        start: searchStart.toISOString(),
        end: searchEnd.toISOString(),
      },
      maxSlots: 1,
    };
  }, [availabilityParams, slotsLoading, slotsError, availableSlots.length, state.slotDate, timeZone]);

  const { data: firstAvailData, isLoading: firstAvailableLoading } =
    useAvailableSlots(firstAvailParams);

  const firstAvailableSlot: AvailableSlotType | null =
    firstAvailData?.slots?.[0] ?? null;

  const handleGoToFirstAvailable = () => {
    if (!firstAvailableSlot) return;
    // Lo slot e' un istante: il giorno su cui posizionarsi e' quello civile
    // dell'organizzazione, non quello del browser di chi guarda.
    const date = civilDayToCarrier(civilDateInTz(parseISO(firstAvailableSlot.start), timeZone));
    dispatch({ type: 'SET_SLOT_DATE', payload: date });
  };

  // ── Multi-day suggestions query (next 7 days) ───────────────────────
  const MULTI_DAY_SEARCH_DAYS = 7;
  const multiDayParams = useMemo(() => {
    if (!availabilityParams) return null;
    // Only fire on step 3
    if (state.step !== 'slot-confirm') return null;

    const fromDay = civilDayOf(state.slotDate);
    const searchStart = startOfCivilDayInTz(fromDay, timeZone);
    const searchEnd = endOfCivilDayInTz(
      addCivilDays(fromDay, MULTI_DAY_SEARCH_DAYS - 1),
      timeZone,
    );

    return {
      ...availabilityParams,
      dateRange: {
        start: searchStart.toISOString(),
        end: searchEnd.toISOString(),
      },
      maxSlots: 50,
    };
  }, [availabilityParams, state.slotDate, state.step, timeZone]);

  const { data: multiDayData, isLoading: multiDaySuggestionsLoading } =
    useAvailableSlots(multiDayParams);

  // Group multi-day results by date
  const multiDaySuggestions = useMemo<DaySuggestion[]>(() => {
    const allSlots = multiDayData?.slots || [];
    if (allSlots.length === 0) return [];

    // Chiave = data civile dell'organizzazione: raggruppare per giorno locale
    // del browser spezzerebbe una giornata in due per i fusi lontani.
    const grouped = new Map<string, AvailableSlotHook[]>();
    for (const slot of allSlots) {
      const dayKey = civilDateInTz(parseISO(slot.start), timeZone);
      if (!grouped.has(dayKey)) grouped.set(dayKey, []);
      grouped.get(dayKey)!.push(slot);
    }

    const currentDay = civilDayOf(state.slotDate);
    const suggestions: DaySuggestion[] = [];
    grouped.forEach((slots, dayKey) => {
      // Exclude the currently viewed day (those slots already show above)
      if (dayKey === currentDay) return;
      const date = civilDayToCarrier(dayKey);
      suggestions.push({
        date,
        dateLabel: format(date, 'EEE d MMM', { locale: dateLocale }),
        slots,
        firstSlotTime: timeStringInTz(parseISO(slots[0].start), timeZone),
        slotCount: slots.length,
      });
    });

    return suggestions.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [multiDayData, state.slotDate, dateLocale, timeZone]);

  // ── Computed slot times ──────────────────────────────────────────────
  const slotStart = useMemo(() => {
    if (state.selectedAvailableSlot) {
      return parseISO(state.selectedAvailableSlot.start);
    }
    if (state.showManualTimeInput && state.manualStartTime && state.slotDate) {
      // L'orario digitato a mano e' quello dell'organizzazione: era qui che
      // "le 9" diventavano un istante letto nel fuso sbagliato.
      const [h, m] = state.manualStartTime.split(':').map(Number);
      return wallClockToUtc(civilDayOf(state.slotDate), h * 60 + m, timeZone);
    }
    return null;
  }, [state.selectedAvailableSlot, state.showManualTimeInput, state.manualStartTime, state.slotDate, timeZone]);

  const slotEnd = useMemo(() => {
    if (!slotStart) return null;
    const dur =
      state.bookingMode === 'custom'
        ? state.customEvent.duration
        : selectedService?.durationMinutes;
    return dur ? addMinutes(slotStart, dur) : null;
  }, [slotStart, selectedService, state.bookingMode, state.customEvent.duration]);

  // ── Handlers ─────────────────────────────────────────────────────────

  // When startFromDate is set, auto-jump slotDate forward if it's before
  useEffect(() => {
    if (!state.smartConstraints.startFromDate) return;
    // Il vincolo e' un istante: il giorno a cui corrisponde va letto nel fuso
    // dell'organizzazione, poi trasportato come carrier.
    const startFrom = civilDayToCarrier(
      civilDateInTz(parseISO(state.smartConstraints.startFromDate), timeZone),
    );
    if (isBefore(state.slotDate, startFrom)) {
      dispatch({ type: 'SET_SLOT_DATE', payload: startFrom });
    }
  }, [state.smartConstraints.startFromDate, state.slotDate, dispatch, timeZone]);

  const handleClose = () => {
    closeModal();
    createAppointment.reset();
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const handleNextStep = () => {
    if (state.step === 'customer-service') {
      if (state.bookingMode === 'custom') {
        if (!state.customEvent.title.trim()) return;
        if (state.customEvent.requiresClient && !state.selectedCustomerId) return;
        dispatch({ type: 'SET_STEP', payload: 'smart-constraints' });
        return;
      }
      // Catalog mode
      if (selectedService?.requiresClient === false && state.selectedServiceId) {
        dispatch({ type: 'SET_STEP', payload: 'smart-constraints' });
      } else if (state.selectedCustomerId && state.selectedServiceId) {
        dispatch({ type: 'SET_STEP', payload: 'smart-constraints' });
      }
    } else if (state.step === 'smart-constraints') {
      dispatch({ type: 'SET_STEP', payload: 'slot-confirm' });
    }
  };

  const handleBackStep = () => {
    if (state.step === 'slot-confirm') {
      dispatch({ type: 'SET_STEP', payload: 'smart-constraints' });
    } else if (state.step === 'smart-constraints') {
      dispatch({ type: 'SET_STEP', payload: 'customer-service' });
    }
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const handleCreateCustomer = async () => {
    const { firstName, lastName, email, phone } = state.newCustomer;
    if (!firstName || !lastName) return;
    try {
      const created = await createCustomerMut.mutateAsync({
        firstName,
        lastName,
        email: email || undefined,
        phone: phone || undefined,
      });
      dispatch({ type: 'CUSTOMER_CREATED', payload: created.id });
    } catch (err) {
      console.error('Failed to create customer:', err);
    }
  };

  const handleSlotSelect = (slot: AvailableSlot) => {
    dispatch({ type: 'SELECT_SLOT', payload: slot });
  };

  const handleDateChange = (direction: 'prev' | 'next') => {
    const offset = direction === 'next' ? 1 : -1;
    const newDate = addDays(state.slotDate, offset);
    // Prevent navigating before startFromDate
    if (state.smartConstraints.startFromDate) {
      const startFrom = civilDayToCarrier(
        civilDateInTz(parseISO(state.smartConstraints.startFromDate), timeZone),
      );
      if (isBefore(newDate, startFrom)) return;
    }
    dispatch({ type: 'SET_SLOT_DATE', payload: newDate });
  };

  // Navigate to a multi-day suggestion: change both booking panel date + scheduler calendar date
  const handleNavigateToSuggestion = useCallback((suggestion: DaySuggestion) => {
    dispatch({ type: 'SET_SLOT_DATE', payload: suggestion.date });
    setDate(suggestion.date); // Sync calendar view
  }, [dispatch, setDate]);

  // ── Preview sync: keep ghostAppointment in scheduler store in sync ───
  const { setPreviewAppointment, setJoinTargetAppointmentId } = useSchedulerActions();

  useEffect(() => {
    if (!slotStart || !slotEnd || state.step !== 'slot-confirm') {
      setPreviewAppointment(null);
      setJoinTargetAppointmentId(null);
      return;
    }
    // Se l'utente sceglie di unirsi a un evento esistente non mostrare il ghost
    // di un nuovo appuntamento: quando resourcesFree=true le allocazioni dello
    // slot puntano a risorse LIBERE (per un eventuale nuovo evento), non a quelle
    // dell'evento a cui ci si aggrega — il ghost cadrebbe sulla risorsa sbagliata.
    // Evidenziamo invece l'evento reale già presente sulla griglia.
    const isJoining = !!state.selectedAvailableSlot?.joinable && state.joinMode === 'join';
    if (isJoining) {
      setPreviewAppointment(null);
      setJoinTargetAppointmentId(state.selectedAvailableSlot!.joinable!.appointmentId);
      return;
    }
    setJoinTargetAppointmentId(null);
    const serviceName = state.bookingMode === 'custom'
      ? state.customEvent.title
      : selectedService?.name || '';
    const serviceColor = state.bookingMode === 'custom'
      ? state.customEvent.color
      : selectedService?.color || null;
    const customerName = selectedCustomer
      ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
      : null;
    const allocations = state.selectedAvailableSlot?.allocations?.map(a => ({
      resourceId: a.resourceId,
      resourceName: a.resourceName,
    })) || [];

    setPreviewAppointment({
      scheduledStart: slotStart.toISOString(),
      scheduledEnd: slotEnd.toISOString(),
      serviceName,
      serviceColor,
      customerName,
      allocations,
    });
  }, [slotStart, slotEnd, state.step, state.bookingMode, state.customEvent.title, state.customEvent.color, selectedService, selectedCustomer, state.selectedAvailableSlot, state.joinMode, setPreviewAppointment, setJoinTargetAppointmentId]);

  // When slot date changes in booking, also sync calendar to see the preview
  useEffect(() => {
    if (state.step === 'slot-confirm') {
      setDate(state.slotDate);
    }
  }, [state.slotDate, state.step, setDate]);

  const handleRequestOptimization = async () => {
    if (!slotStart || !state.selectedServiceId) return;
    dispatch({ type: 'SET_OPTIMIZATION', payload: null });
    dispatch({ type: 'SET_SHOW_OPTIMIZATION_PANEL', payload: true });
    try {
      const result = await optimizeSuggestionMut.mutateAsync({
        desiredDateTime: slotStart.toISOString(),
        serviceDefinitionId: state.selectedServiceId,
        durationMinutes: selectedService?.durationMinutes,
      });
      dispatch({ type: 'SET_OPTIMIZATION', payload: result });
    } catch (err) {
      console.error('Optimization request failed:', err);
      dispatch({
        type: 'SET_OPTIMIZATION',
        payload: { canOptimize: false, reason: i18n.t('booking.optimizeError', { ns: 'scheduler' }) },
      });
    }
  };

  const handleApplyOptimizationAndBook = async () => {
    const opt = state.optimizationSuggestion;
    if (!opt?.canOptimize || !opt.blockingAppointment || !opt.suggestedMove) return;
    if (!state.selectedCustomerId || !state.selectedServiceId || !slotStart || !slotEnd) return;

    const allocations = opt.suggestedMove.availableResources.map((r, i) => ({
      resourceId: r.resourceId,
      role: i === 0 ? 'primary' : 'support',
    }));

    if (!allocations.length) {
      dispatch({ type: 'SET_ERROR', payload: i18n.t('booking.noResourcesInFreedSlot', { ns: 'scheduler' }) });
      return;
    }

    try {
      await overrideBookingMut.mutateAsync({
        moveAppointmentId: opt.blockingAppointment.id,
        moveToTime: opt.suggestedMove.newStart,
        newAppointment: {
          serviceDefinitionId: state.selectedServiceId,
          customerId: state.selectedCustomerId,
          scheduledStart: slotStart.toISOString(),
          scheduledEnd: slotEnd.toISOString(),
          allocations,
        },
      });
      handleClose();
    } catch (error: any) {
      const msg =
        error.response?.data?.message || i18n.t('booking.overrideError', { ns: 'scheduler' });
      dispatch({ type: 'SET_ERROR', payload: msg });
    }
  };

  const handleSubmit = () => {
    if (!slotStart || !slotEnd) return;
    dispatch({ type: 'CLEAR_ERROR' });

    const allocations =
      state.selectedAvailableSlot?.allocations?.map(a => ({ resourceId: a.resourceId })) || [];

    // ---- Custom event mode ----
    if (state.bookingMode === 'custom') {
      if (!state.customEvent.title.trim()) return;
      if (state.customEvent.requiresClient && !state.selectedCustomerId) return;

      createAppointment.mutate(
        {
          title: state.customEvent.title,
          duration: state.customEvent.duration,
          color: state.customEvent.color,
          customerId: state.customEvent.requiresClient ? state.selectedCustomerId : undefined,
          scheduledStart: slotStart.toISOString(),
          scheduledEnd: slotEnd.toISOString(),
          allocations: allocations.length > 0 ? allocations : undefined,
          customResources:
            state.customEvent.resourceRequirements.length > 0
              ? state.customEvent.resourceRequirements
              : undefined,
          // Capienza evento di gruppo (solo con cliente)
          ...(state.customEvent.requiresClient &&
            state.customEvent.maxClients > 1 && { maxClients: state.customEvent.maxClients }),
        },
        {
          onSuccess: () => handleClose(),
          onError: (error: any) => {
            console.error('Failed to create custom appointment:', error);
            const msg =
              error.response?.data?.message ||
              i18n.t('booking.customEventError', { ns: 'scheduler' });
            dispatch({ type: 'SET_ERROR', payload: msg });
            if (
              msg.includes('occupata') ||
              msg.includes('conflict') ||
              msg.includes('disponibile')
            ) {
              refetchSlots();
            }
          },
        },
      );
      return;
    }

    // ---- Catalog mode ----
    const requiresCustomer = selectedService?.requiresClient !== false;
    if (requiresCustomer && !state.selectedCustomerId) return;
    if (!state.selectedServiceId) return;

    // Join: lo slot selezionato punta a un evento di gruppo esistente con posti
    // liberi → aggiungi il cliente a quell'evento invece di crearne uno nuovo
    const joinable = state.selectedAvailableSlot?.joinable;
    if (joinable && state.joinMode === 'join' && state.selectedCustomerId) {
      addParticipantMut.mutate(
        { appointmentId: joinable.appointmentId, customerId: state.selectedCustomerId },
        {
          onSuccess: () => handleClose(),
          onError: (error: any) => {
            console.error('Failed to join group event:', error);
            const msg =
              error.response?.data?.message ||
              i18n.t('booking.joinError', { ns: 'scheduler' });
            dispatch({ type: 'SET_ERROR', payload: msg });
            refetchSlots();
          },
        },
      );
      return;
    }

    createAppointment.mutate(
      {
        serviceDefinitionId: state.selectedServiceId,
        customerId: requiresCustomer ? state.selectedCustomerId : undefined,
        scheduledStart: slotStart.toISOString(),
        scheduledEnd: slotEnd.toISOString(),
        allocations: allocations.length > 0 ? allocations : undefined,
        // Override capienza (null = usa il default del servizio, materializzato dal backend)
        ...(state.maxClientsOverride !== null && { maxClients: state.maxClientsOverride }),
      },
      {
        onSuccess: () => handleClose(),
        onError: (error: any) => {
          console.error('Failed to create appointment:', error);
          const msg =
            error.response?.data?.message ||
            i18n.t('booking.createError', { ns: 'scheduler' });
          dispatch({ type: 'SET_ERROR', payload: msg });
          if (
            msg.includes('occupata') ||
            msg.includes('conflict') ||
            msg.includes('disponibile')
          ) {
            refetchSlots();
          }
        },
      },
    );
  };

  // ── Build context value ──────────────────────────────────────────────
  const value: BookingContextValue = {
    state,
    dispatch,
    services,
    customers,
    resourceTypes,
    allResources,
    servicesLoading,
    customersLoading,
    availableSlots,
    slotsLoading,
    slotsError,
    refetchSlots,
    firstAvailableSlot,
    firstAvailableLoading,
    handleGoToFirstAvailable,
    multiDaySuggestions,
    multiDaySuggestionsLoading,
    handleNavigateToSuggestion,
    createAppointment,
    createCustomer: createCustomerMut,
    optimizeSuggestion: optimizeSuggestionMut,
    overrideBooking: overrideBookingMut,
    addParticipant: addParticipantMut,
    selectedService,
    selectedCustomer,
    slotStart,
    slotEnd,
    handleClose,
    handleNextStep,
    handleBackStep,
    handleSubmit,
    handleCreateCustomer,
    handleSlotSelect,
    handleDateChange,
    handleRequestOptimization,
    handleApplyOptimizationAndBook,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}
